import dayjs from "dayjs";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { message, Modal } from "antd";

import useOptions from "features/masterData/hooks/useOptions";
import { generateRandomString } from "utils/helper";
import * as services from "../services/companyApi";
import { setCurrentWorking, setIsLoading } from "../slices/companySlice";
import { useTranslation } from "react-i18next";

function toInitialValues(initialValues = {}) {

  function timeToMinutes(timeString) {
    if (!timeString) return 0;

    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  const { shiftConditionChange = {} } = initialValues;
  const modifiedValues = {
    ...initialValues,
    baseOnShifts: initialValues.baseOnShifts === "Y",
    checkOutByLocation: initialValues.checkOutByLocation === "Y",
    halfProrateFromDay: initialValues.halfProrateFromDay === "Y",
    onlyEmployeeNormalStatus: initialValues.onlyEmployeeNormalStatus === "Y",
    shiftConditionChange: shiftConditionChange.id || null,
    supvAnyTimeShiftChange: initialValues.supvAnyTimeShiftChange === "Y",
    yearEndDate:
      initialValues.yearEndDate &&
        initialValues.yearEndDate !== null &&
        initialValues.yearEndDate !== "0000-00-00"
        ? dayjs(
          initialValues.yearEndDate.replace("9999", new Date().getFullYear()),
          "DD-MM-YYYY"
        )
        : dayjs(),
    lateTime: initialValues?.lateTime ? timeToMinutes(String(initialValues?.lateTime)) : "00",
    absentTime: initialValues?.absentTime ? timeToMinutes(String(initialValues?.absentTime)) : "00",
  };
  return modifiedValues;
}

function toParams(values) {
  const modifiedValues = {
    ...values,
    baseOnShifts: values.baseOnShifts ? "Y" : "N",
    checkOutByLocation: values.checkOutByLocation ? "Y" : "N",
    halfProrateFromDay: values.halfProrateFromDay ? "Y" : "N",
    onlyEmployeeNormalStatus: values.onlyEmployeeNormalStatus ? "Y" : "N",
    supvAnyTimeShiftChange: values.supvAnyTimeShiftChange ? "Y" : "N",
    yearEndDate: values.yearEndDate
      ? values.yearEndDate.format("DD-MM-9999")
      : "0000-00-00",
  };
  return modifiedValues;
}

function useCompanyWorking(props) {
  const { t, i18n } = useTranslation();
  const { onCancel = () => { } } = props;
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.company.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentWorking = useSelector((state) => state.company.currentWorking);

  const { shiftConditionChangeOptions = [], getShiftConditionChangeOptions } =
    useOptions();

  const initialValues = useMemo(() => {
    return toInitialValues(currentWorking);
  }, [currentWorking]);

  const getCompanyWorking = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getWorkingCondition({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      dispatch(setCurrentWorking(response.data));
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch]);

  const addWorkingCondition = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.addWorkingCondition(values);
        message.success(t("common.success"))
        onCancel();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, onCancel]
  );

  const editWorkingCondition = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.editWorkingCondition(values);
        message.success(t("common.success"))
        onCancel();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, onCancel]
  );

  function minuteCal(params) {
    if (!params) return "00:00:00";

    let totalMinutes = parseInt(params, 10) || 0;
    let hours = Math.floor(totalMinutes / 60);
    let minutes = totalMinutes % 60;
    let seconds = 0;

    let formattedHours = String(hours).padStart(2, "0");
    let formattedMinutes = String(minutes).padStart(2, "0");
    let formattedSeconds = String(seconds).padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  }

  const onSubmit = useCallback(
    async (values) => {
      const modifiedValues = toParams({
        ...values,
        companyId: currentCompany,
        lateTime: minuteCal(values?.lateTime),
        absentTime: minuteCal(values?.absentTime)
      });
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? "Are you sure confirm to edit company working condition ?"
          : "คุณยืนยันการแก้ไขเงื่อนไขการทำงานทั่วไปใช่หรือไม่ ?"
        ),
        onOk() {
          if (currentWorking.workingConditionId) {
            editWorkingCondition(modifiedValues);
          } else {
            addWorkingCondition(modifiedValues);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [currentCompany, currentWorking, addWorkingCondition, editWorkingCondition]
  );

  const handleCancel = useCallback(() => {
    Modal.confirm({
      title: t("dialog.confirmation.header"),
      content:
        i18n.language === "en"
          ? "Are you sure you want to cancel the changes?"
          : "คุณต้องการยกเลิกการแก้ไขใช่หรือไม่ ?",
      onOk() {
        onCancel();
      },
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
    });
  }, [onCancel, t, i18n.language]);

  useEffect(() => {
    getCompanyWorking();
  }, [getCompanyWorking]);

  useEffect(() => {
    getShiftConditionChangeOptions();
  }, [getShiftConditionChangeOptions]);

  return {
    isLoading,
    initialValues,
    currentWorking,
    shiftConditionChangeOptions,
    onEdit: props.onEdit,
    onCancel: handleCancel,
    onSubmit,
  };
}

export default useCompanyWorking;
